// สแกนกระดาษคำตอบ — อัปโหลด PDF จากเครื่องสแกน (หรือรูป) ทั้งห้อง ระบบอ่านทุกแผ่น แล้วให้ครูตรวจก่อนบันทึก
//
// อ่านในเบราว์เซอร์ทั้งหมด (omr/reader.js) ภาพไม่ถูกส่งขึ้นเซิร์ฟเวอร์ สิ่งที่บันทึกคือคำตอบรายข้อเท่านั้น
// ผ่าน endpoint เดียวกับการกรอกมือ (source = 'scan' — แผ่นว่างได้ศูนย์ และคะแนนอัตนัยเดิมไม่ถูกแตะ)
//
// แผ่นที่มีข้อไม่แน่ใจ (ฝนจาง ลบไม่สะอาด ฝนสองช่อง) ไม่ถูกเลือกให้บันทึกจนกว่าครูจะดูภาพแล้วกด "ตรวจแล้ว"
// หรือแก้คำตอบเอง — ตัวอ่านที่เดาเงียบๆ แล้วลงคะแนนผิดแย่กว่าตัวอ่านที่ถามบ่อย
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Badge, Button, Card, Form, Modal, ProgressBar } from 'react-bootstrap'
import { Link, useParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, readerText as toText, savedText, isObjective, roomLabel, showError } from './examUtils'
import { readSheet } from './omr/reader'
import { isSupported, loadPages } from './omr/loadPages'
import SheetOverlay, { UncertainList } from './SheetOverlay'

const FILTERS = [
  { key: 'all', label: 'ทั้งหมด' },
  { key: 'review', label: 'ต้องตรวจ' },
  { key: 'problem', label: 'มีปัญหา' },
  { key: 'ready', label: 'พร้อมบันทึก' },
]


let nextId = 1

export default function ExamScan() {
  const { examId } = useParams()
  const { setLoading } = useLoading()
  const [, setSkin] = useState('')
  const [data, setData] = useState(null)
  const [sheets, setSheets] = useState([])
  const [progress, setProgress] = useState(null)   // { done, running }
  const [filter, setFilter] = useState('all')
  const [viewId, setViewId] = useState(null)
  const fileRef = useRef(null)

  const load = useCallback(() => axiosInstance.get(`${API}/exams/${examId}/results`)
    .then((res) => setData(res.data))
    .catch((err) => showError(err, 'โหลดข้อสอบไม่สำเร็จ')), [examId])

  useEffect(() => { load() }, [load])

  const objective = useMemo(() => (data ? data.questions.filter(isObjective) : []), [data])
  const studentByStudy = useMemo(() => new Map((data?.students || []).map((s) => [s.study_id, s])), [data])

  const update = (id, patch) => setSheets((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)))

  // สถานะที่คำนวณจากข้อมูลปัจจุบัน (ไม่เก็บลง state เพราะเปลี่ยนตามการแก้ของครู)
  const describe = useCallback((sheet) => {
    const student = sheet.studyId ? studentByStudy.get(sheet.studyId) : null
    const dupes = sheet.studyId ? sheets.filter((s) => s.studyId === sheet.studyId && s.include && !s.saved).length : 0
    let diff = 0
    if (student?.answers && sheet.text !== null) {
      const before = savedText(student.answers, objective)
      diff = [...sheet.text].filter((ch, i) => ch !== before[i]).length
    }
    const problem = !student ? (sheet.studyId ? 'นักเรียนไม่ได้อยู่ในห้องที่ใช้ข้อสอบนี้' : sheet.error || 'ยังไม่ได้เลือกนักเรียน')
      : sheet.text === null ? sheet.error
        : dupes > 1 ? 'มีหลายแผ่นของนักเรียนคนเดียวกัน — เลือกบันทึกแผ่นเดียว'
          : null
    const needsReview = !problem && sheet.uncertain.length > 0 && !sheet.reviewed
    // แผ่นซ้ำต้องยังกดสลับได้ ไม่งั้นครูเลือกทั้งสองแผ่นแล้วปิดไม่ได้สักแผ่น
    const dupe = !!student && sheet.text !== null && dupes > 1
    return { student, problem, needsReview, diff, dupe, ready: !problem && !needsReview && sheet.include && !sheet.saved }
  }, [studentByStudy, sheets, objective])

  const onFiles = async (fileList) => {
    const files = [...fileList].filter(isSupported)
    if (!files.length || !data) return
    setProgress({ done: 0, running: true })
    try {
      for await (const page of loadPages(files)) {
        const res = readSheet(page.img, data.exam, data.questions)
        const sheet = {
          id: nextId++,
          label: page.label,
          thumb: page.thumb,
          studyId: res.ok ? res.studyId : null,
          text: res.answers ? toText(res.answers, objective) : null,
          uncertain: res.uncertain || [],
          rows: res.rows || [],
          markers: res.markers || null,
          error: res.ok ? null : res.error,
          reviewed: false,
          saved: false,
        }
        setSheets((prev) => {
          // แผ่นซ้ำของคนเดิม: แผ่นแรกถูกเลือกไว้ แผ่นหลังไม่ถูกเลือก ให้ครูตัดสินเอง
          const dupe = sheet.studyId && prev.some((s) => s.studyId === sheet.studyId && !s.saved)
          return [...prev, { ...sheet, include: res.ok && !dupe }]
        })
        setProgress((p) => ({ ...p, done: p.done + 1 }))
        await new Promise((r) => setTimeout(r, 0))   // ให้หน้าจอได้วาดความคืบหน้า
      }
    } catch (err) {
      showError(err, 'เปิดไฟล์ไม่สำเร็จ')
    } finally {
      setProgress((p) => ({ ...p, running: false }))
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  const described = sheets.map((s) => ({ sheet: s, info: describe(s) }))
  const visible = described.filter(({ sheet, info }) => {
    if (filter === 'review') return info.needsReview
    if (filter === 'problem') return !!info.problem && !sheet.saved
    if (filter === 'ready') return info.ready
    return true
  })
  const ready = described.filter(({ info }) => info.ready)
  const counts = {
    review: described.filter(({ info }) => info.needsReview).length,
    problem: described.filter(({ sheet, info }) => info.problem && !sheet.saved).length,
    ready: ready.length,
  }

  const save = async () => {
    if (!ready.length) return
    const replacing = ready.filter(({ info }) => info.student.answers && info.diff > 0).length
    const ok = await Swal.fire({
      icon: 'question',
      title: `บันทึกคำตอบจากการสแกน ${ready.length} แผ่น?`,
      html: replacing
        ? `มี ${replacing} คนที่มีคำตอบเดิมอยู่แล้วและจะถูกแทนที่ด้วยผลสแกน<br/>คะแนนอัตนัยที่กรอกไว้ไม่ถูกแตะ`
        : 'คะแนนอัตนัยที่กรอกไว้ไม่ถูกแตะ',
      showCancelButton: true,
      confirmButtonText: 'บันทึก',
      cancelButtonText: 'ยกเลิก',
    })
    if (!ok.isConfirmed) return

    const byRoom = new Map()
    for (const { sheet, info } of ready) {
      const list = byRoom.get(info.student.teach_id) || []
      const chars = [...sheet.text]
      list.push({ id: sheet.id, study_id: sheet.studyId, answers: Object.fromEntries(objective.map((q, i) => [q.eq_no, chars[i] ?? ''])) })
      byRoom.set(info.student.teach_id, list)
    }
    setLoading(true)
    const savedIds = []
    try {
      for (const [teachId, list] of byRoom) {
        await axiosInstance.put(`${API}/exams/${examId}/rooms/${teachId}/responses`, {
          source: 'scan',
          responses: list.map(({ study_id, answers }) => ({ study_id, answers })),
        })
        savedIds.push(...list.map((r) => r.id))
      }
      Swal.fire({ icon: 'success', title: `บันทึกแล้ว ${savedIds.length} แผ่น`, timer: 1600, showConfirmButton: false })
    } catch (err) {
      showError(err, savedIds.length ? `บันทึกได้ ${savedIds.length} แผ่น แล้วหยุด` : 'บันทึกไม่สำเร็จ')
    } finally {
      const done = new Set(savedIds)
      setSheets((prev) => prev.map((s) => (done.has(s.id) ? { ...s, saved: true } : s)))
      await load()
      setLoading(false)
    }
  }

  if (!data) {
    return (<><Header onSkin={setSkin} /><div className="main main-app p-2 p-lg-4" /></>)
  }

  const viewing = sheets.find((s) => s.id === viewId)
  const reviewQueue = described.filter(({ info }) => info.needsReview).map(({ sheet }) => sheet.id)

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-start gap-2 mb-3">
          <div className="me-auto">
            <Link to={`/teaching/exams/${examId}`} className="fs-sm"><i className="ri-arrow-left-line"></i> {data.exam.exam_name}</Link>
            <h4 className="main-title mb-0">สแกนกระดาษคำตอบ</h4>
            <div className="fs-sm text-secondary">
              อัปโหลดไฟล์ PDF จากเครื่องสแกน หรือรูปถ่ายกระดาษ — อ่านในเครื่องนี้ ไม่ได้ส่งภาพขึ้นเซิร์ฟเวอร์
            </div>
          </div>
          <Button size="sm" disabled={!ready.length} onClick={save}>
            <i className="ri-save-line"></i> บันทึก {ready.length} แผ่น
          </Button>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Form.Control
              ref={fileRef}
              type="file"
              multiple
              accept="application/pdf,image/jpeg,image/png,image/webp"
              disabled={progress?.running}
              onChange={(e) => onFiles(e.target.files)}
            />
            <div className="fs-xs text-secondary mt-2">
              สแกนขาวดำหรือเทา 200–300 dpi ได้ผลดีที่สุด · หลายห้องปนกันในไฟล์เดียวได้ ระบบแยกให้จากรหัสแผ่น ·
              รูปถ่ายต้องเห็นสี่เหลี่ยมดำครบ 4 มุม วางกระดาษบนพื้นเรียบ แสงสม่ำเสมอ
            </div>
            {progress && (
              <div className="mt-2">
                {progress.running
                  ? <ProgressBar animated now={100} label={`กำลังอ่าน… ${progress.done} แผ่น`} />
                  : <div className="fs-sm">อ่านแล้ว {sheets.length} แผ่น</div>}
              </div>
            )}
          </Card.Body>
        </Card>

        {sheets.length > 0 && (
          <Card className="card-one">
            <Card.Header className="d-flex flex-wrap align-items-center gap-2">
              <div className="d-flex flex-wrap gap-1 me-auto">
                {FILTERS.map((f) => (
                  <Button key={f.key} size="sm" variant={filter === f.key ? 'primary' : 'outline-primary'} onClick={() => setFilter(f.key)}>
                    {f.label}{f.key !== 'all' && counts[f.key] > 0 ? ` (${counts[f.key]})` : ''}
                  </Button>
                ))}
              </div>
              {reviewQueue.length > 0 && (
                <Button size="sm" variant="warning" onClick={() => setViewId(reviewQueue[0])}>
                  <i className="ri-eye-line"></i> เริ่มตรวจแผ่นที่ไม่แน่ใจ
                </Button>
              )}
            </Card.Header>
            <Card.Body className="p-0">
              {visible.map(({ sheet, info }) => (
                <SheetRow key={sheet.id} sheet={sheet} info={info} objective={objective}
                  students={data.students} onChange={(patch) => update(sheet.id, patch)} onView={() => setViewId(sheet.id)} />
              ))}
              {!visible.length && <div className="text-secondary text-center py-4">ไม่มีแผ่นในกลุ่มนี้</div>}
            </Card.Body>
          </Card>
        )}
        <Footer />
      </div>

      <SheetViewer
        sheet={viewing}
        info={viewing ? describe(viewing) : null}
        objective={objective}
        onHide={() => setViewId(null)}
        onChange={(patch) => viewing && update(viewing.id, patch)}
        onConfirm={() => {
          if (!viewing) return
          update(viewing.id, { reviewed: true, include: true })
          const next = reviewQueue.find((id) => id !== viewing.id)
          setViewId(next ?? null)
        }}
      />
    </>
  )
}

// ---- แถวของแต่ละแผ่น ----

function SheetRow({ sheet, info, objective, students, onChange, onView }) {
  const uncertainIdx = new Set(sheet.uncertain.map((no) => objective.findIndex((q) => q.eq_no === no)))
  return (
    <div className={`d-flex flex-wrap gap-3 p-3 border-bottom ${sheet.saved ? 'opacity-50' : ''}`}>
      <button type="button" className="border-0 bg-transparent p-0" onClick={onView} title="ดูภาพ">
        <img src={sheet.thumb.url} alt="" style={{ width: 64, height: 90, objectFit: 'cover' }} className="border rounded" />
      </button>
      <div className="flex-grow-1" style={{ minWidth: 220 }}>
        <div className="fs-xs text-secondary">{sheet.label}</div>
        {info.student && sheet.studyId && !sheet.error ? (
          <div className="fw-semibold">
            {info.student.st_name}
            <span className="text-secondary fw-normal"> · {roomLabel(info.student)} เลขที่ {info.student.seat}</span>
          </div>
        ) : (
          <Form.Select size="sm" className="mt-1" style={{ maxWidth: 360 }} value={sheet.studyId ?? ''}
            onChange={(e) => onChange({ studyId: Number(e.target.value) || null, include: !!e.target.value })}>
            <option value="">— เลือกนักเรียน —</option>
            {students.map((s) => (
              <option key={s.study_id} value={s.study_id}>{roomLabel(s)} เลขที่ {s.seat} {s.st_name}</option>
            ))}
          </Form.Select>
        )}
        <div className="d-flex flex-wrap gap-1 mt-1">
          {sheet.saved && <Badge bg="success">บันทึกแล้ว</Badge>}
          {info.problem && !sheet.saved && <Badge bg="danger">{info.problem}</Badge>}
          {sheet.error && info.student && !info.problem && <Badge bg="secondary">เลือกนักเรียนเอง</Badge>}
          {sheet.uncertain.length > 0 && !sheet.reviewed && <Badge bg="warning" text="dark">ไม่แน่ใจ {sheet.uncertain.length} ข้อ: {sheet.uncertain.join(', ')}</Badge>}
          {sheet.reviewed && <Badge bg="info">ตรวจแล้ว</Badge>}
          {info.student?.answers && !sheet.saved && (
            <Badge bg="light" text="dark" className="border">
              มีคำตอบเดิม{info.diff ? ` · ต่าง ${info.diff} ข้อ` : ' · ตรงกัน'}
            </Badge>
          )}
        </div>
      </div>
      {sheet.text !== null && (
        <div style={{ minWidth: 260, flex: '2 1 260px' }}>
          <Form.Control
            size="sm"
            className="font-monospace"
            style={{ letterSpacing: 2 }}
            value={sheet.text}
            maxLength={objective.length}
            disabled={sheet.saved}
            onChange={(e) => onChange({ text: e.target.value, reviewed: true, include: true })}
          />
          <div className="font-monospace fs-xs text-nowrap overflow-hidden" style={{ letterSpacing: 2, paddingLeft: 9 }}>
            {[...sheet.text].map((ch, i) => (
              <span key={i} className={uncertainIdx.has(i) && !sheet.reviewed ? 'bg-warning' : 'text-secondary'}>
                {uncertainIdx.has(i) ? '^' : ' '}
              </span>
            ))}
          </div>
        </div>
      )}
      <Form.Check
        type="switch"
        id={`inc-${sheet.id}`}
        label="บันทึก"
        className="align-self-center"
        disabled={sheet.saved || (!!info.problem && !info.dupe)}
        checked={sheet.include && (!info.problem || info.dupe)}
        onChange={(e) => onChange({ include: e.target.checked, reviewed: sheet.reviewed || e.target.checked })}
      />
    </div>
  )
}

// ---- ดูภาพพร้อมสิ่งที่ตัวอ่านเห็น ----

function SheetViewer({ sheet, info, objective, onHide, onChange, onConfirm }) {
  if (!sheet) return null
  const { thumb } = sheet
  return (
    <Modal show onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title as="h6">
          {info?.student ? `${info.student.st_name} · ${roomLabel(info.student)}` : sheet.label}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-wrap gap-3">
          <div className="flex-shrink-0" style={{ maxWidth: '100%' }}>
            <SheetOverlay thumb={thumb} markers={sheet.markers} rows={sheet.rows} text={sheet.text}
              objective={objective} uncertain={sheet.uncertain} />
          </div>
          <div className="flex-grow-1" style={{ minWidth: 240 }}>
            {sheet.error && <Alert variant="warning" className="fs-sm py-2">{sheet.error}</Alert>}
            <div className="fs-sm mb-2">
              <span className="me-2"><span className="d-inline-block rounded-circle me-1" style={{ width: 10, height: 10, background: 'rgba(25,135,84,.6)' }} />คำตอบที่อ่านได้</span>
              <span><span className="d-inline-block rounded-circle me-1 border border-2" style={{ width: 10, height: 10, borderColor: '#fd7e14' }} />ข้อที่ไม่แน่ใจ</span>
            </div>
            {sheet.text !== null && (
              <>
                <Form.Label className="fs-sm">คำตอบ (แก้ได้)</Form.Label>
                <Form.Control size="sm" className="font-monospace mb-2" style={{ letterSpacing: 2 }} value={sheet.text}
                  maxLength={objective.length} disabled={sheet.saved}
                  onChange={(e) => onChange({ text: e.target.value, reviewed: true, include: true })} />
                <UncertainList uncertain={sheet.uncertain} rows={sheet.rows} />
              </>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide}>ปิด</Button>
        {!sheet.saved && !info?.problem && sheet.text !== null && (
          <Button onClick={onConfirm}><i className="ri-check-line"></i> ตรวจแล้ว ถัดไป</Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
